import type {
  GroupedSelectMenuOption,
  SelectMenuOption,
} from 'src/components/SelectMenu/select-menu.types';
import type { ConversationAssigneeGroupOption } from 'src/graphql/entities/conversation-assignee-group';
import type { UserGroupOption } from 'src/graphql/entities/user-group';

/** The two dimensions a macro's audience can target. */
export enum MacroAudienceKind {
  UserGroup = 'userGroup',
  AssigneeGroup = 'assigneeGroup',
}

const KIND_LABELS: Record<MacroAudienceKind, string> = {
  [MacroAudienceKind.UserGroup]: 'User groups',
  [MacroAudienceKind.AssigneeGroup]: 'Assignee groups',
};

/**
 * Lucide icon per kind, so the two are told apart wherever they are listed.
 * Matches the design reference: a people glyph for permission roles, a headset
 * for the support teams conversations route to.
 */
export const AUDIENCE_KIND_ICONS: Record<MacroAudienceKind, string> = {
  [MacroAudienceKind.UserGroup]: 'users',
  [MacroAudienceKind.AssigneeGroup]: 'headphones',
};

const VALUE_SEPARATOR = ':';

/**
 * A macro's audience as the form holds it: the two kinds stay separate, because
 * they are persisted to separate join tables.
 */
export interface MacroAudienceSelection {
  userGroups: UserGroupOption[];
  conversationAssigneeGroups: ConversationAssigneeGroupOption[];
}

/**
 * One selected group in the audience control. The `GroupedSelectMenu` works in
 * flat string values, so every option carries a kind-prefixed value
 * (`userGroup:<id>`) — the two id spaces are independent and can collide.
 */
export function encodeAudienceValue(kind: MacroAudienceKind, groupId: string): string {
  return `${kind}${VALUE_SEPARATOR}${groupId}`;
}

export function decodeAudienceValue(
  value: string,
): { kind: MacroAudienceKind; groupId: string } | null {
  const separatorIndex = value.indexOf(VALUE_SEPARATOR);
  if (separatorIndex <= 0) {
    return null;
  }

  const kind = value.slice(0, separatorIndex) as MacroAudienceKind;
  if (!Object.values(MacroAudienceKind).includes(kind)) {
    return null;
  }

  // Group ids never contain the separator, but slicing on the first one keeps
  // the id intact if that ever changes.
  return { kind, groupId: value.slice(separatorIndex + VALUE_SEPARATOR.length) };
}

function toChildOption(kind: MacroAudienceKind, group: Option): SelectMenuOption {
  return {
    value: encodeAudienceValue(kind, String(group.value)),
    title: group.label,
  };
}

/**
 * The menu's two top-level rows — *User groups* and *Assignee groups* — each
 * carrying its groups as children.
 */
export function buildAudienceOptions(
  available: MacroAudienceSelection,
): GroupedSelectMenuOption[] {
  return [
    {
      value: MacroAudienceKind.UserGroup,
      title: KIND_LABELS[MacroAudienceKind.UserGroup],
      children: available.userGroups.map((group) =>
        toChildOption(MacroAudienceKind.UserGroup, group),
      ),
    },
    {
      value: MacroAudienceKind.AssigneeGroup,
      title: KIND_LABELS[MacroAudienceKind.AssigneeGroup],
      children: available.conversationAssigneeGroups.map((group) =>
        toChildOption(MacroAudienceKind.AssigneeGroup, group),
      ),
    },
  ];
}

/** The selected values, for the controlled `GroupedSelectMenu`. */
export function toAudienceValues(selected: MacroAudienceSelection): string[] {
  return [
    ...selected.userGroups.map((group) =>
      encodeAudienceValue(MacroAudienceKind.UserGroup, String(group.value)),
    ),
    ...selected.conversationAssigneeGroups.map((group) =>
      encodeAudienceValue(MacroAudienceKind.AssigneeGroup, String(group.value)),
    ),
  ];
}

function resolveOption<T extends Option>(
  groupId: string,
  available: T[],
  previous: T[],
): T | null {
  return (
    available.find((option) => String(option.value) === groupId) ??
    previous.find((option) => String(option.value) === groupId) ??
    null
  );
}

/**
 * Splits the menu's flat values back into the two form fields, resolving each
 * against the available options. A value with no matching option is kept as a
 * fallback entry labelled by its id, so a group the current user cannot see
 * (a platform group behind `access_to_platform_user_groups`) survives a save
 * instead of being silently dropped.
 */
export function fromAudienceValues(
  values: string[],
  available: MacroAudienceSelection,
  previous: MacroAudienceSelection,
): MacroAudienceSelection {
  const result: MacroAudienceSelection = {
    userGroups: [],
    conversationAssigneeGroups: [],
  };

  values.forEach((value) => {
    const decoded = decodeAudienceValue(value);
    if (!decoded) {
      return;
    }

    if (decoded.kind === MacroAudienceKind.UserGroup) {
      const option = resolveOption(decoded.groupId, available.userGroups, previous.userGroups);
      if (option) {
        result.userGroups.push(option);
      }
      return;
    }

    const option = resolveOption(
      decoded.groupId,
      available.conversationAssigneeGroups,
      previous.conversationAssigneeGroups,
    );
    if (option) {
      result.conversationAssigneeGroups.push(option);
    }
  });

  return result;
}
